import { calcHash } from '../../../../../libs/file';
import { getIndividualValidationStatus } from '../../helpers/individualValidation';
import { ValidationCheck } from '../../../../../consts/validationCheck';
import { IndividualValidationStatus } from '../../../../../consts/individualValidationStatus';
import L from 'leaflet';
import { Blue20, Blue80, Red30, Red80 } from '@traceair/tds';
import React, { useRef, useState } from 'react';
import { UploadData } from '../../../../../store/uploadStore';

interface CircleState {
  lon: number;
  lat: number;
  id: string;
}

export const useDrawScanProgress = (
  mapInstanceRef: React.MutableRefObject<L.Map | null>,
  scanUpload: UploadData | undefined,
  boundaryLayerRefBorder: React.MutableRefObject<L.Polygon | null> = useRef(null),
  boundaryLayerRef: React.MutableRefObject<L.Polygon | null> = useRef(null),
) => {
  const [updateForcer, forceUpdate] = useState(false);

  const lastCallTimeRef = useRef<number>(0);
  const circlesRef = useRef<Record<string, L.Circle>>({});

  const drawScanProgress = (throttle = false) => {
    if (!mapInstanceRef.current) return;

    const mapInstance = mapInstanceRef.current;

    const photosToGenerateProgressDots = [...(scanUpload?.upload?.photos || []), ...(scanUpload?.queue || [])].filter(
      ({ fileCategory, coords }) => fileCategory === 'image' && coords && coords.lon && coords.lat,
    );

    if (photosToGenerateProgressDots.length > 300) {
      // Tail-throttle map redraws for large scans to 0.5s, extra-large scans to 1s
      const throttleTime = photosToGenerateProgressDots.length < 1000 ? 500 : 1000;
      const elapsed = Date.now() - lastCallTimeRef.current;
      if (throttle && elapsed < throttleTime) {
        setTimeout(() => forceUpdate((prev) => !prev), throttleTime - elapsed);
        return;
      }
    }

    lastCallTimeRef.current = Date.now();

    if (photosToGenerateProgressDots.length) {
      boundaryLayerRefBorder.current?.setStyle({ stroke: true });
      boundaryLayerRef.current?.setStyle({ fillOpacity: 0, weight: 1 });
    } else {
      boundaryLayerRefBorder.current?.setStyle({ stroke: false });
      boundaryLayerRef.current?.setStyle({ fill: true, weight: 2, fillOpacity: 0.5 });

      Object.values(circlesRef.current).forEach((circle) => {
        if (mapInstance.hasLayer(circle)) {
          mapInstance.removeLayer(circle);
        }
      });
      circlesRef.current = {};
      boundaryLayerRefBorder.current?.redraw();
      boundaryLayerRef.current?.redraw();
      return;
    }

    const validCoords: CircleState[] = [];
    const invalidCoords: CircleState[] = [];
    const uploadedValidCoords: CircleState[] = [];
    const uploadedInvalidCoords: CircleState[] = [];
    const activeCircleIds = new Set();

    photosToGenerateProgressDots.forEach((photo) => {
      const { coords, uploaded, validation } = photo;
      if (!coords || !validation) return;

      const hash = 'hash' in photo ? photo.hash : calcHash(photo.file);
      const id = `${coords.lat}_${coords.lon}_${hash}`;
      activeCircleIds.add(id);

      const hasErrors = Object.entries(validation).some(
        ([vKey, result]) =>
          getIndividualValidationStatus(vKey as ValidationCheck, result[0]) === IndividualValidationStatus.Error,
      );

      if (uploaded) {
        if (hasErrors) {
          uploadedInvalidCoords.push({ ...coords, id });
        } else {
          uploadedValidCoords.push({ ...coords, id });
        }
      } else {
        if (hasErrors) {
          invalidCoords.push({ ...coords, id });
        } else {
          validCoords.push({ ...coords, id });
        }
      }
    });

    // Remove circles that no longer exist.
    Object.keys(circlesRef.current).forEach((id) => {
      if (!activeCircleIds.has(id)) {
        if (mapInstance.hasLayer(circlesRef.current[id])) {
          mapInstance.removeLayer(circlesRef.current[id]);
        }
        delete circlesRef.current[id];
      }
    });

    function updateOrCreateCircles(coordsList: { lon: number; lat: number; id: string }[], color: string) {
      coordsList.forEach(({ lon, lat, id }) => {
        if (circlesRef.current[id]) {
          circlesRef.current[id].setStyle({ color, fillColor: color, fillOpacity: 1 }).bringToFront();
        } else {
          const circle = L.circle([lat, lon], {
            radius: 20,
            color,
            fillColor: color,
            fillOpacity: 1,
            weight: 1,
          });
          circle.addTo(mapInstance).bringToFront();
          circlesRef.current[id] = circle;
        }
      });
    }

    if (validCoords.length + invalidCoords.length + uploadedValidCoords.length + uploadedInvalidCoords.length > 0) {
      updateOrCreateCircles(validCoords, Blue20);
      updateOrCreateCircles(invalidCoords, Red30);
      updateOrCreateCircles(uploadedValidCoords, Blue80);
      updateOrCreateCircles(uploadedInvalidCoords, Red80);
    }
  };

  return {
    drawScanProgress,
    circlesRef,
    updateForcer,
  };
};
